import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { SupportedTokenName } from "@mezo/sdk"
import { Block, LabelMedium, useStyletron } from "@mezo-org/mezo-clay"
import { useField, useFormikContext } from "formik"

import {
  RootState,
  selectIsConnected,
  selectTokenDecimals,
  useDappSelector,
} from "#/redux-state"
import TokenAmountInput, {
  TokenAmountInputProps,
} from "#/shared/components/TokenAmountInput"
import TokenAmountInputMaxButton from "#/shared/components/TokenAmountInput/TokenAmountInputMaxButton"
import TokenSelect from "#/shared/components/TokenSelect"
import { ALL_TOKENS, PORTAL_TOKENS } from "#/shared/constants"
import { FlowContext } from "#/shared/context"
import { bigIntToFloat, floatToBigInt } from "#/shared/utils"
import DepositToken from "#/ui-old/Deposit/DepositToken/DepositToken"

import ReadOnlyAssetInput from "./ReadonlyAssetInput"

type FieldAssetInputProps = {
  label?: string
  withMaxButton?: boolean
  selectTokenCallback?: (token: SupportedTokenName) => void
  tokens?: string[]
  readOnly?: boolean
} & Partial<TokenAmountInputProps>

const defaultTokens = PORTAL_TOKENS.map((_) => _.token)

// This component is based on an old input that was built with Chakra UI. It
// should be removed when we fully switch to the Clay lib.
export default function FieldAssetInput({
  withMaxButton,
  maxTokenAmount,
  selectTokenCallback,
  label = "Amount",
  tokens = defaultTokens,
  readOnly = false,
  ...props
}: FieldAssetInputProps) {
  const [css, theme] = useStyletron()

  const isConnected = useDappSelector(selectIsConnected)
  const { isBitcoinFlow } = useContext(FlowContext)

  const [, metaToken, helpersToken] = useField<SupportedTokenName>("token")

  const [, metaAmount, helpersAmount] = useField<bigint | undefined>("amount")

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, metaToken.value),
  )

  const [displayedValue, setDisplayedValue] = useState<string>(
    metaAmount.value
      ? bigIntToFloat(metaAmount.value, tokenDecimals, tokenDecimals)
      : "",
  )

  useEffect(() => {
    if (!displayedValue) {
      helpersAmount.setValue(undefined)
      return
    }

    const tokenAmount = floatToBigInt(displayedValue, tokenDecimals)
    helpersAmount.setValue(tokenAmount)
  }, [displayedValue, helpersAmount, tokenDecimals])

  const { setErrors } = useFormikContext()

  const handleErrorCallback = useCallback(
    (hasError: boolean, errorMessage: string | JSX.Element) => {
      if (hasError) {
        if (typeof errorMessage === "string") {
          helpersAmount.setError(errorMessage)
        } else {
          helpersAmount.setError("Invalid value.")
        }
      } else {
        setErrors({})
      }
    },
    [helpersAmount, setErrors],
  )

  const inputToken = useMemo(() => {
    if (isBitcoinFlow) {
      return <DepositToken token="BTC" />
    }

    return (
      <TokenSelect
        activeToken={metaToken.value}
        selectCallback={(selectedToken: SupportedTokenName) => {
          helpersToken.setValue(selectedToken)
          selectTokenCallback?.(selectedToken)
        }}
        tokens={ALL_TOKENS.filter(({ token }) => tokens.includes(token))}
        withTokenBalance
      />
    )
  }, [
    helpersToken,
    isBitcoinFlow,
    metaToken.value,
    selectTokenCallback,
    tokens,
  ])

  if (readOnly) {
    return (
      <ReadOnlyAssetInput
        amount={bigIntToFloat(
          metaAmount.value ?? 0n,
          Number(tokenDecimals),
          Number(tokenDecimals),
        )}
        token={metaToken.value}
      />
    )
  }

  return (
    <Block
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: theme.sizing.scale300,
      })}
    >
      <LabelMedium color={theme.colors.contentTertiary}>{label}</LabelMedium>
      <Block position="relative">
        <TokenAmountInput
          placeholder="0"
          token={metaToken.value}
          tokenDecimals={tokenDecimals}
          precisionAmount={tokenDecimals}
          tokenAmount={displayedValue}
          maxTokenAmount={maxTokenAmount}
          setTokenAmountCallback={setDisplayedValue}
          errorCallback={handleErrorCallback}
          paddingRight="165px"
          fontSize="32px"
          height="80px"
          {...props}
        />
        {withMaxButton && (
          <TokenAmountInputMaxButton
            maxTokenAmount={maxTokenAmount ?? "0"}
            setTokenAmount={setDisplayedValue}
            isDisabled={!isConnected}
            right="86px"
            top="30px"
            backgroundColor={theme.colors.primaryA}
            px={2}
            py={1}
          >
            max
          </TokenAmountInputMaxButton>
        )}
        {inputToken}
      </Block>
    </Block>
  )
}
